import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Printer, Timer } from 'lucide-react';
import { useDopamenu } from '../contexts/DopamenuContext';
import { MAX_ACTIVITY_LENGTH, MAX_MENU_ITEMS, MENU_CATEGORIES, MENU_THEMES } from '../utils/dopamenu';
import DopamenuPrint from './DopamenuPrint';
import { ToolButton, toolTextSx } from './ToolCard';

export default function DopamenuBuilder() {
  const { state, dispatch, storageUnavailable } = useDopamenu();
  const [drafts, setDrafts] = useState(() => Object.fromEntries(MENU_CATEGORIES.map(({ id }) => [id,
    state.items.filter((item) => item.category === id).map((item) => item.title).join('\n')])));
  const [errors, setErrors] = useState({});
  const [printError, setPrintError] = useState(false);

  useEffect(() => {
    document.body.classList.add('dopamenu-printing');
    return () => document.body.classList.remove('dopamenu-printing');
  }, []);

  const editCategory = (category, text) => {
    setDrafts((current) => ({ ...current, [category]: text }));
    const titles = text.split(/\r?\n/).map((title) => title.trim()).filter(Boolean);
    const others = state.items.filter((item) => item.category !== category);
    const seen = new Set(others.map((item) => item.title.toLowerCase()));
    let error = '';
    if (titles.some((title) => title.length > MAX_ACTIVITY_LENGTH)) error = `Keep each activity to ${MAX_ACTIVITY_LENGTH} characters or fewer to save this section.`;
    else if (titles.length + others.length > MAX_MENU_ITEMS) error = `Your menu can hold ${MAX_MENU_ITEMS} activities. Remove a line to save this section.`;
    else titles.forEach((title) => {
      if (seen.has(title.toLowerCase())) error = `“${title}” is already on your menu. Remove the duplicate to save this section.`;
      seen.add(title.toLowerCase());
    });
    setErrors((current) => ({ ...current, [category]: error }));
    if (error) return;
    const existing = state.items.filter((item) => item.category === category);
    dispatch({ type: 'category', category, entries: titles.map((title, index) => ({
      id: existing[index]?.id || window.crypto?.randomUUID?.() || `activity-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
      title,
    })) });
  };

  const print = () => {
    try {
      if (typeof window.print !== 'function') throw new Error('Print unavailable');
      window.print();
      setPrintError(false);
    } catch (_error) {
      setPrintError(true);
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <style>{'@media print { @page { size: auto; margin: 12mm; } }'}</style>
      {storageUnavailable && <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-message': toolTextSx }}>This browser can’t save your menu right now. You can still use it and print a copy before leaving.</Alert>}
      <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" gap={1.5} role="group" aria-label="Print colors" flexWrap="wrap">
          {Object.entries(MENU_THEMES).map(([id, colors]) => <ToolButton key={id} aria-pressed={state.theme === id}
            onClick={() => dispatch({ type: 'theme', value: id })} startIcon={<Box component="span" sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors.accent, flexShrink: 0 }} />}
            sx={{ fontSize: '0.6rem', px: 1.5, ...(state.theme === id && { borderColor: 'secondary.main' }) }}>{colors.name}</ToolButton>)}
        </Stack>
        <ToolButton variant="contained" startIcon={<Printer size={20} />} onClick={print} disabled={Object.values(errors).some(Boolean)}>Print / Save PDF</ToolButton>
      </Stack>
      {printError && <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-message': toolTextSx }}>Use your browser’s Print option to print this menu or save it as a PDF.</Alert>}
      <Box component="section" aria-label="Editable dopamine menu">
        <DopamenuPrint menu={state} drafts={drafts} errors={errors}
          onTitleChange={(value) => dispatch({ type: 'title', value })} onCategoryChange={editCategory} />
      </Box>
      <Stack gap={2} alignItems="flex-start" sx={{ mt: 4 }}>
        <Typography sx={{ ...toolTextSx, color: 'text.primary' }}>Saved in this browser.</Typography>
        <ToolButton component={Link} to="/tools/pomodoro" startIcon={<Timer size={20} />}>Use with the Pomodoro timer</ToolButton>
      </Stack>
      {createPortal(<div className="dopamenu-print-root" aria-hidden="true"><DopamenuPrint menu={state} /></div>, document.body)}
    </Box>
  );
}
